import { useEffect, useMemo, useState } from "react";
import { AlertTriangle, Download, Info, Loader2, ShieldX } from "lucide-react";
import { PageHeader } from "@/components/common/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Tabs, TabsList, TabsTrigger, TabsContent,
} from "@/components/ui/tabs";
import {
  Table, TableHeader, TableRow, TableHead, TableBody, TableCell,
} from "@/components/ui/table";
import { api, apiError } from "@/lib/api";
import { mxn } from "@/lib/format";
import { toast } from "sonner";

const TIPO_BADGE = {
  activo: { label: "Activo", cls: "bg-emerald-100 text-emerald-700" },
  pasivo: { label: "Pasivo", cls: "bg-rose-100 text-rose-700" },
  capital: { label: "Capital", cls: "bg-indigo-100 text-indigo-700" },
  resultados: { label: "Resultados", cls: "bg-amber-100 text-amber-700" },
  orden: { label: "Orden", cls: "bg-slate-100 text-slate-600" },
};

function ultimoDiaMes(yyyyMm) {
  if (!/^\d{4}-\d{2}$/.test(yyyyMm || "")) return "";
  const [y, m] = yyyyMm.split("-").map(Number);
  const d = new Date(y, m, 0).getDate(); // día 0 del mes siguiente = último día del mes actual
  return `${yyyyMm}-${String(d).padStart(2, "0")}`;
}

function BannerPreliminar() {
  return (
    <div
      data-testid="banner-preliminar"
      className="mb-4 flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-[13px] text-amber-900"
    >
      <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
      <div>
        <span className="font-semibold">Estados financieros preliminares e informativos.</span>{" "}
        Cifras derivadas automáticamente de la operación, sujetas a revisión y ajuste contable.
        No constituyen estados financieros formales.
      </div>
    </div>
  );
}

function TipoBadge({ tipo }) {
  const b = TIPO_BADGE[tipo];
  if (!b) return <span className="text-xs text-slate-400">—</span>;
  return (
    <span className={`inline-flex rounded-full px-2 py-0.5 text-[11px] font-medium ${b.cls}`}>
      {b.label}
    </span>
  );
}

export default function EstadosFinancieros() {
  // Periodo (para Balanza y Resultados) y fecha (para Balance).
  const [periodo, setPeriodo] = useState("");
  const [fecha, setFecha] = useState("");
  const [tab, setTab] = useState("balance");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [balanza, setBalanza] = useState(null);
  const [resultados, setResultados] = useState(null);
  const [balance, setBalance] = useState(null);
  const [exporting, setExporting] = useState(false);

  // Al montar: usar mes con actividad más reciente. Si no hay, mes actual.
  useEffect(() => {
    (async () => {
      let per = "";
      try {
        const r = await api.get("/fiscal/mes-actividad");
        if (r.data?.mes_mas_reciente && /^\d{4}-\d{2}$/.test(r.data.mes_mas_reciente)) {
          per = r.data.mes_mas_reciente;
        }
      } catch { /* silencioso */ }
      if (!per) {
        const d = new Date();
        per = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
      }
      setPeriodo(per);
      setFecha(ultimoDiaMes(per));
    })();
  }, []);

  // Carga la data cuando el usuario cambia periodo o fecha.
  useEffect(() => {
    if (!periodo || !fecha) return;
    (async () => {
      setLoading(true);
      try {
        const [bz, er, bg] = await Promise.all([
          api.get(`/fiscal/estados/balanza?periodo=${periodo}`).then((r) => r.data),
          api.get(`/fiscal/estados/resultados?periodo=${periodo}`).then((r) => r.data),
          api.get(`/fiscal/estados/balance?fecha=${fecha}`).then((r) => r.data),
        ]);
        setBalanza(bz);
        setResultados(er);
        setBalance(bg);
        setError(false);
      } catch (e) {
        if (e?.response?.status === 403) setError(true);
        else toast.error(apiError(e));
      } finally {
        setLoading(false);
      }
    })();
  }, [periodo, fecha]);

  const exportar = async () => {
    setExporting(true);
    try {
      const res = await api.get(
        `/fiscal/estados/export?periodo=${periodo}&fecha=${fecha}`,
        { responseType: "blob" }
      );
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `estados_prelim_${periodo}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Estados financieros preliminares descargados (XLSX)");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setExporting(false);
    }
  };

  const totalesBalanceOk = useMemo(() => balance?.cuadra, [balance]);

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-24 text-center" data-testid="estados-error">
        <ShieldX className="h-10 w-10 text-red-400" />
        <p className="mt-3 text-sm text-slate-500">
          No tienes acceso a los estados financieros de Contabilidad y Fiscal.
        </p>
      </div>
    );
  }

  return (
    <div data-testid="estados-financieros-page">
      <PageHeader
        title="Estados financieros"
        subtitle="Balance General, Estado de Resultados y Balanza — versión preliminar derivada de la operación"
      >
        <div className="flex flex-wrap items-end gap-2">
          <div>
            <Label className="text-[11px] text-slate-500">Periodo (Balanza / Resultados)</Label>
            <Input
              data-testid="periodo-input"
              type="month"
              value={periodo}
              className="h-9 w-40"
              onChange={(e) => {
                setPeriodo(e.target.value);
                setFecha(ultimoDiaMes(e.target.value));
              }}
            />
          </div>
          <div>
            <Label className="text-[11px] text-slate-500">Fecha de corte (Balance)</Label>
            <Input
              data-testid="fecha-input"
              type="date"
              value={fecha}
              className="h-9 w-44"
              onChange={(e) => setFecha(e.target.value)}
            />
          </div>
          <Button
            data-testid="exportar-estados-btn"
            size="sm"
            className="bg-primary hover:bg-sidebar-hover"
            onClick={exportar}
            disabled={exporting || loading || !periodo || !fecha}
          >
            {exporting ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Download className="mr-1.5 h-4 w-4" />} Exportar XLSX
          </Button>
        </div>
      </PageHeader>

      {loading ? (
        <div className="flex items-center justify-center py-24">
          <Loader2 className="h-7 w-7 animate-spin text-primary" />
        </div>
      ) : (
        <div className="p-6 md:p-8">
          <BannerPreliminar />

          <Tabs value={tab} onValueChange={setTab}>
            <TabsList data-testid="estados-tabs">
              <TabsTrigger value="balance" data-testid="tab-balance">Balance General</TabsTrigger>
              <TabsTrigger value="resultados" data-testid="tab-resultados">Estado de Resultados</TabsTrigger>
              <TabsTrigger value="balanza" data-testid="tab-balanza">Balanza de comprobación</TabsTrigger>
            </TabsList>

            {/* --------- BALANCE GENERAL --------- */}
            <TabsContent value="balance" className="mt-4">
              {balance && (
                <>
                  <p className="mb-3 flex items-center gap-1.5 text-xs text-slate-500" data-testid="balance-nota">
                    <Info className="h-3.5 w-3.5" /> {balance.nota_preliminar}
                  </p>

                  <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                    <BloqueBalance titulo="Activo" filas={balance.activo} total={balance.total_activo} testid="bloque-activo" />
                    <div className="space-y-4">
                      <BloqueBalance titulo="Pasivo" filas={balance.pasivo} total={balance.total_pasivo} testid="bloque-pasivo" />
                      <BloqueBalance titulo="Capital contable" filas={balance.capital} total={balance.total_capital} testid="bloque-capital" />
                    </div>
                  </div>

                  <div className="mt-6 rounded-lg border border-slate-200 bg-white p-4">
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-3 text-sm">
                      <Linea label="Total Activo" value={balance.total_activo} accent="text-emerald-700" testid="total-activo" />
                      <Linea label="Total Pasivo + Capital" value={balance.total_pasivo_mas_capital} accent="text-slate-800" testid="total-pas-cap" />
                      <Linea label="Diferencia" value={balance.diferencia}
                        accent={totalesBalanceOk ? "text-emerald-600" : "text-amber-600"}
                        testid="diferencia-balance" />
                    </div>
                    <div className="mt-2 text-xs" data-testid="cuadre-badge">
                      {totalesBalanceOk ? (
                        <Badge className="bg-emerald-100 text-emerald-700">Cuadra: Activo = Pasivo + Capital</Badge>
                      ) : (
                        <Badge className="bg-amber-100 text-amber-700">No cuadra — normal en versión preliminar sin saldos iniciales sellados</Badge>
                      )}
                    </div>
                  </div>

                  {balance.orden?.length > 0 && (
                    <div className="mt-6">
                      <h3 className="mb-2 text-sm font-semibold text-slate-600">Cuentas de orden (no afectan el cuadre)</h3>
                      <BloqueBalance titulo="Orden" filas={balance.orden} total={balance.total_orden} testid="bloque-orden" hideTitulo />
                    </div>
                  )}
                </>
              )}
            </TabsContent>

            {/* --------- ESTADO DE RESULTADOS --------- */}
            <TabsContent value="resultados" className="mt-4">
              {resultados && (
                <div className="space-y-4">
                  <h3 className="text-sm font-semibold text-slate-700">Ingresos</h3>
                  <TablaResultados filas={resultados.ingresos} total={resultados.total_ingresos} totalLabel="TOTAL INGRESOS" testid="tabla-ingresos" />
                  <h3 className="mt-4 text-sm font-semibold text-slate-700">Gastos / Costos</h3>
                  <TablaResultados filas={resultados.gastos} total={resultados.total_gastos} totalLabel="TOTAL GASTOS" testid="tabla-gastos" />
                  <div className="mt-6 rounded-lg border border-slate-200 bg-white p-4">
                    <div className="flex items-center justify-between text-sm">
                      <span className="font-semibold text-slate-700">
                        {resultados.resultado_tipo === "utilidad" ? "UTILIDAD" : "PÉRDIDA"} DEL PERIODO
                      </span>
                      <span
                        className={`text-lg font-semibold tabular-nums ${resultados.utilidad_del_periodo >= 0 ? "text-emerald-700" : "text-red-600"}`}
                        data-testid="utilidad-periodo"
                      >
                        {mxn(resultados.utilidad_del_periodo)}
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </TabsContent>

            {/* --------- BALANZA DE COMPROBACIÓN --------- */}
            <TabsContent value="balanza" className="mt-4">
              {balanza && (
                <>
                  <p className="mb-3 flex items-center gap-1.5 text-xs text-slate-500" data-testid="balanza-nota">
                    <Info className="h-3.5 w-3.5" /> {balanza.nota_saldo_inicial}
                  </p>
                  <div className="overflow-hidden rounded-lg border border-slate-200 bg-white">
                    <Table data-testid="tabla-balanza">
                      <TableHeader>
                        <TableRow>
                          <TableHead>Código</TableHead>
                          <TableHead>Nombre</TableHead>
                          <TableHead>Agrupador</TableHead>
                          <TableHead>Tipo</TableHead>
                          <TableHead className="text-right">Saldo inicial</TableHead>
                          <TableHead className="text-right">Cargos</TableHead>
                          <TableHead className="text-right">Abonos</TableHead>
                          <TableHead className="text-right">Saldo final</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {balanza.filas.map((f) => (
                          <TableRow key={f.codigo} data-testid={`balanza-row-${f.codigo}`}>
                            <TableCell className="font-mono text-xs tabular-nums">{f.codigo}</TableCell>
                            <TableCell className="text-sm">{f.nombre}</TableCell>
                            <TableCell className="font-mono text-xs tabular-nums">{f.agrupador || "—"}</TableCell>
                            <TableCell><TipoBadge tipo={f.tipo_cuenta} /></TableCell>
                            <TableCell className="text-right tabular-nums text-slate-400">{mxn(f.saldo_inicial)}</TableCell>
                            <TableCell className="text-right tabular-nums">{mxn(f.cargos)}</TableCell>
                            <TableCell className="text-right tabular-nums">{mxn(f.abonos)}</TableCell>
                            <TableCell className="text-right tabular-nums font-medium">{mxn(f.saldo_final)}</TableCell>
                          </TableRow>
                        ))}
                        {balanza.filas.length === 0 && (
                          <TableRow>
                            <TableCell colSpan={8} className="py-8 text-center text-sm text-slate-400">
                              Sin movimientos en el periodo.
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                    <div className="grid grid-cols-3 gap-4 border-t border-slate-200 bg-slate-50 px-4 py-3 text-sm">
                      <div>
                        <span className="text-slate-500">Total cargos: </span>
                        <span className="font-semibold tabular-nums" data-testid="total-cargos">{mxn(balanza.total_cargos)}</span>
                      </div>
                      <div>
                        <span className="text-slate-500">Total abonos: </span>
                        <span className="font-semibold tabular-nums" data-testid="total-abonos">{mxn(balanza.total_abonos)}</span>
                      </div>
                      <div className="text-right" data-testid="balanza-cuadre">
                        {balanza.balanceada ? (
                          <Badge className="bg-emerald-100 text-emerald-700">Balanceada</Badge>
                        ) : (
                          <Badge className="bg-amber-100 text-amber-700">Descuadrada</Badge>
                        )}
                      </div>
                    </div>
                  </div>
                </>
              )}
            </TabsContent>
          </Tabs>
        </div>
      )}
    </div>
  );
}

function Linea({ label, value, accent = "text-slate-800", testid }) {
  return (
    <div className="flex flex-col rounded-md border border-slate-100 bg-slate-50 px-3 py-2">
      <span className="text-[11px] uppercase tracking-wide text-slate-500">{label}</span>
      <span className={`text-base font-semibold tabular-nums ${accent}`} data-testid={testid}>{mxn(value ?? 0)}</span>
    </div>
  );
}

function BloqueBalance({ titulo, filas, total, testid, hideTitulo = false }) {
  return (
    <div className="overflow-hidden rounded-lg border border-slate-200 bg-white" data-testid={testid}>
      {!hideTitulo && (
        <div className="border-b border-slate-100 bg-slate-50 px-4 py-2 text-sm font-semibold text-slate-700">
          {titulo}
        </div>
      )}
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Código</TableHead>
            <TableHead>Nombre</TableHead>
            <TableHead className="text-right">Saldo</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {filas.map((f) => (
            <TableRow key={f.codigo} data-testid={`${testid}-row-${f.codigo}`}>
              <TableCell className="font-mono text-xs tabular-nums">{f.codigo}</TableCell>
              <TableCell className="text-sm">
                {f.nombre}
                {f.sintetico && <span className="ml-2 text-[10px] text-slate-400">(sintética)</span>}
              </TableCell>
              <TableCell className="text-right tabular-nums">{mxn(f.saldo)}</TableCell>
            </TableRow>
          ))}
          {filas.length === 0 && (
            <TableRow>
              <TableCell colSpan={3} className="py-6 text-center text-xs text-slate-400">
                Sin movimientos.
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
      <div className="flex items-center justify-between border-t border-slate-200 bg-slate-50 px-4 py-2 text-sm">
        <span className="font-semibold text-slate-600">Total {titulo.toLowerCase()}</span>
        <span className="font-semibold tabular-nums" data-testid={`${testid}-total`}>{mxn(total)}</span>
      </div>
    </div>
  );
}

function TablaResultados({ filas, total, totalLabel, testid }) {
  return (
    <div className="overflow-hidden rounded-lg border border-slate-200 bg-white">
      <Table data-testid={testid}>
        <TableHeader>
          <TableRow>
            <TableHead>Código</TableHead>
            <TableHead>Nombre</TableHead>
            <TableHead>Agrupador</TableHead>
            <TableHead className="text-right">Importe</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {filas.map((f) => (
            <TableRow key={f.codigo} data-testid={`${testid}-row-${f.codigo}`}>
              <TableCell className="font-mono text-xs tabular-nums">{f.codigo}</TableCell>
              <TableCell className="text-sm">{f.nombre}</TableCell>
              <TableCell className="font-mono text-xs tabular-nums">{f.agrupador || "—"}</TableCell>
              <TableCell className="text-right tabular-nums">{mxn(f.saldo_final)}</TableCell>
            </TableRow>
          ))}
          {filas.length === 0 && (
            <TableRow>
              <TableCell colSpan={4} className="py-6 text-center text-xs text-slate-400">
                Sin movimientos en el periodo.
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
      <div className="flex items-center justify-between border-t border-slate-200 bg-slate-50 px-4 py-2 text-sm">
        <span className="font-semibold text-slate-600">{totalLabel}</span>
        <span className="font-semibold tabular-nums" data-testid={`${testid}-total`}>{mxn(total)}</span>
      </div>
    </div>
  );
}
